/**
 * Model style: Kenney low-poly GLTF assets, lazy-loaded from /models. Anything that is
 * missing falls back to the blocky builder so the game never breaks without assets.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { VehicleDef } from '@typing-racer/shared';
import type { BuiltVehicle, StyleRenderer } from '../style.js';
import { blockyStyle, driver, mat } from './blocky.js';

const loader = new GLTFLoader();
const cache = new Map<string, Promise<THREE.Group | null>>();

function load(file: string): Promise<THREE.Group | null> {
  let p = cache.get(file);
  if (!p) {
    p = loader
      .loadAsync(`/models/${file}`)
      .then((gltf) => gltf.scene)
      .catch(() => {
        console.info(`[model style] ${file} not found, using blocky fallback`);
        return null;
      });
    cache.set(file, p);
  }
  return p;
}

export function tint(root: THREE.Object3D, colour: string): THREE.Object3D[] {
  const wheels: THREE.Object3D[] = [];
  const c = new THREE.Color(colour);
  root.traverse((o) => {
    if (o.name.toLowerCase().includes('wheel')) wheels.push(o);
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      m.castShadow = true;
      m.receiveShadow = true;
      const multiple = Array.isArray(m.material);
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      const tinted = mats.map((material) => {
        const clone = (material as THREE.MeshStandardMaterial).clone();
        if (/body|paint|main/i.test(clone.name) || mats.length === 1) clone.color = c;
        return clone;
      });
      m.material = multiple ? tinted : tinted[0]!;
    }
  });
  return wheels;
}

export const modelStyle: StyleRenderer = {
  id: 'model',
  async buildVehicle(def: VehicleDef, colour: string): Promise<BuiltVehicle> {
    const src = await load(def.model);
    if (!src) return blockyStyle.buildVehicle(def, colour);
    const root = new THREE.Group();
    const model = src.clone(true);
    const wheels = tint(model, colour);
    // Kenney vehicles face -Z at ~1 unit wide; our vehicles face +Z at def.width.
    const bbox = new THREE.Box3().setFromObject(model);
    const size = bbox.getSize(new THREE.Vector3());
    const s = def.length / Math.max(size.z, 0.001);
    model.scale.setScalar(s);
    model.rotation.y = Math.PI;
    model.position.y = -bbox.min.y * s;
    root.add(model);
    const d = driver(0.6);
    d.position.set(0, size.y * s * 0.45, 0);
    root.add(d);
    return { root, wheels };
  },
  async buildTree(rng) {
    const src = await load('tree.glb');
    if (!src) return blockyStyle.buildTree(rng);
    const t = src.clone(true);
    t.scale.setScalar(2 + rng() * 2);
    t.rotation.y = rng() * Math.PI * 2;
    t.traverse((o) => {
      o.castShadow = true;
    });
    return t;
  },
  async buildBuilding(rng) {
    const src = await load('building.glb');
    if (!src) return blockyStyle.buildBuilding(rng);
    const b = src.clone(true);
    b.scale.setScalar(6 + rng() * 6);
    b.traverse((o) => {
      o.castShadow = true;
      o.receiveShadow = true;
    });
    return b;
  },
  groundMaterial: () => mat('#66bb6a'),
  roadMaterial: () => mat('#505a64'),
};
